import React, { FC, ReactNode, useRef, useState } from 'react';
import type { FormStatus } from '@wix/form-viewer';
import type { FormValues, FormViewerHandle } from '@wix/form-viewer/widget';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { allowsFutureStartDate } from '@wix/pricing-plans-utils';
import { useStyles } from '@wix/tpa-settings/react';
import {
  Text,
  TextTypography,
  Button,
  ButtonPriority,
  ThreeDotsLoader,
  Dialog,
  Divider,
  HoverStyles,
} from '@wix/wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { withClientSideRenderMark } from '../../../contexts/ClientSideRenderMark';
import { useLocale } from '../../../hooks/useLocale';
import { fullDate, ymdToDate } from '../../../utils/date';
import { clampToEarliestStartDate } from '../../../utils/date-constraints';
import { Toast } from '../../PackagePicker/Widget/Toast';
import componentStyles from '../stylesParams';
import { PlanCustomizationProps } from '../types';
import { AdditionalDetailsLoader } from './AdditionalDetails/AdditionalDetailsLoader';
import { EditorPreviewNotification } from './EditorPreviewNotification/EditorPreviewNotification';
import { st, classes } from './PlanCustomizationWidget.st.css';
import { PlanPreview } from './PlanPreview/PlanPreview';
import { PlanStartDatePickerLoader } from './PlanStartDatePicker/PlanStartDatePickerLoader';

const LazyPlanStartDatePicker = loadable(() => import('./PlanStartDatePicker/PlanStartDatePicker'), {
  fallback: <PlanStartDatePickerLoader />,
});
const LazyAdditionalDetails = loadable(() => import('./AdditionalDetails/AdditionalDetails'), {
  fallback: <AdditionalDetailsLoader />,
});

const ProductPageWidget: React.FC<PlanCustomizationProps> = (props) => {
  const styles = useStyles();
  const { t } = useTranslation();

  if (props.status === 'PAGE_LOADING') {
    return null;
  }

  const hasShadow = styles.get(componentStyles.planFormEnableShadow);
  const hasBorder = !!styles.get(componentStyles.planFormBorderWidth);

  return (
    <>
      {props.additionalLineItemsError && <AdditionalLineItemsErrorModal />}
      <EditorPreviewNotification />
      {props.statusMessage && (
        <Toast informational={props.status !== 'ERROR'} onClose={() => props.clearStatusMessage()}>
          {props.statusMessage}
        </Toast>
      )}
      <div className={classes.root}>
        {styles.get(componentStyles.showPlanCard) && (
          <aside className={classes.previewContainer}>
            <PlanPreview plan={props.plan} variant={props.variant} />
          </aside>
        )}
        <main
          className={st(classes.contentContainer, {
            'with-padding': hasShadow || hasBorder,
            'with-shadow': hasShadow,
          })}
        >
          {styles.get(componentStyles.showPageTitle) && (
            <header className={classes.header}>
              <Text
                className={classes.pageTitle}
                tagName={
                  styles.get(componentStyles.pageTitleFont).htmlTag ??
                  styles.getDefaultValue(componentStyles.pageTitleFont).htmlTag
                }
              >
                {t('plan-customization.widget.form-title')}
              </Text>
              <Divider className={classes.headerDivider} />
            </header>
          )}
          <ProductCustomizationForm {...props} />
        </main>
      </div>
    </>
  );
};

const AdditionalLineItemsErrorModal: FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const goBack = () => window.history.back();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      notFullscreenOnMobile
      data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_ADDITIONAL_ITEMS_ERROR_MODAL}
      appendToBody
      contentClassName={classes.modalContent}
      childrenWrapperClassName={classes.modalContentWrapper}
    >
      <div className={classes.modal}>
        <Text className={classes.errorModalTitle} typography={TextTypography.largeTitle}>
          {t('plan-customization.additional-items-error.title')}
        </Text>
        <Text className={classes.errorModalSubtitle} typography={TextTypography.runningText}>
          {t('plan-customization.additional-items-error.subtitle')}
        </Text>
        <Button
          className={classes.errorModalButton}
          wrapContent
          upgrade
          hoverStyle={HoverStyles.underline}
          onClick={goBack}
        >
          {t('plan-customization.additional-items-error.button')}
        </Button>
      </div>
    </Dialog>
  );
};

type StartDateView = 'FIXED' | 'PICKER' | 'HIDDEN';

function getStartDateView(plan: {
  allowsFutureStartDate: boolean;
  hasSingleAvailableStartDate: boolean;
  disableAllDates: boolean;
  hasStartDate: boolean;
}): StartDateView {
  if (!plan.allowsFutureStartDate) {
    return 'HIDDEN';
  }

  if (plan.hasSingleAvailableStartDate && !plan.disableAllDates && plan.hasStartDate) {
    return 'FIXED';
  }

  return 'PICKER';
}

const ProductCustomizationForm: FC<PlanCustomizationProps> = ({
  plan,
  variant,
  status,
  formControllerStatus,
  proceedToPayment,
  minStartDate,
  maxStartDate,
  initialStartDate,
  disableAllDates,
  noAvailableDatesMessage,
  hasSingleAvailableStartDate,
  usePlanCustomization,
  startDateConstraints,
  isCartEnabled,
}) => {
  const formViewerRef = useRef<FormViewerHandle>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(() => {
    if (usePlanCustomization) {
      return startDateConstraints?.initialStartDate ? new Date(startDateConstraints.initialStartDate) : undefined;
    }
    return disableAllDates ? undefined : new Date(initialStartDate ?? minStartDate ?? Date.now());
  });

  const earliestStartDate = startDateConstraints?.earliestStartDate
    ? new Date(startDateConstraints.earliestStartDate)
    : undefined;
  const latestStartDateExclusive = startDateConstraints?.latestStartDateExclusive
    ? new Date(startDateConstraints.latestStartDateExclusive)
    : undefined;

  const { t } = useTranslation();

  const isCheckoutLoading = status === 'CHECKOUT_LOADING';
  const hasConnectedForm = !!plan.formId;
  const displayedStartDate = !usePlanCustomization && initialStartDate ? ymdToDate(initialStartDate) : customStartDate;
  const startDateView = getStartDateView({
    allowsFutureStartDate: allowsFutureStartDate(variant),
    hasSingleAvailableStartDate: Boolean(hasSingleAvailableStartDate),
    disableAllDates: Boolean(disableAllDates),
    hasStartDate: Boolean(displayedStartDate),
  });

  const [startDateLoaded, setStartDateLoaded] = useState(false);
  const [formsComponentLoaded, setFormsComponentLoaded] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>('READY');
  const isFormBusy = hasConnectedForm && formStatus === 'BUSY';

  const onContinueClick = async () => {
    if (isFormBusy) {
      return;
    }

    // TODO: What to do if (hasConnectedForm && !formViewerRef.current) ?
    if (hasConnectedForm && formViewerRef.current) {
      const isValid = await formViewerRef.current?.validate();

      if (!isValid) {
        return;
      }
    }

    const submittedStartDate =
      usePlanCustomization && customStartDate
        ? clampToEarliestStartDate(customStartDate, earliestStartDate)
        : customStartDate;

    proceedToPayment({
      startDate:
        allowsFutureStartDate(variant) && submittedStartDate && !disableAllDates ? submittedStartDate.toString() : null,
      formValues: hasConnectedForm ? formValues : null,
    });
  };

  const isButtonDisabled = () => {
    if (isCheckoutLoading) {
      return true;
    }

    if (startDateView === 'PICKER' && !startDateLoaded) {
      return true;
    }

    if (allowsFutureStartDate(variant) && disableAllDates) {
      return true;
    }

    if (hasConnectedForm && !formsComponentLoaded) {
      return true;
    }

    if (isFormBusy) {
      return true;
    }

    return false;
  };

  return (
    <div>
      {startDateView === 'PICKER' && (
        <LazyPlanStartDatePicker
          className={classes.datePicker}
          value={customStartDate}
          onChange={(date) => setCustomStartDate(date)}
          min={minStartDate}
          max={maxStartDate}
          earliestStartDate={earliestStartDate}
          latestStartDateExclusive={latestStartDateExclusive}
          disableAllDates={disableAllDates}
          noAvailableDatesMessage={noAvailableDatesMessage}
          onLoad={() => setStartDateLoaded(true)}
        />
      )}
      {startDateView === 'FIXED' && <FixedStartDate date={displayedStartDate!} />}
      {hasConnectedForm && (
        <LazyAdditionalDetails
          plan={plan}
          formValues={formValues}
          setFormValues={setFormValues}
          ref={formViewerRef}
          formControllerStatus={formControllerStatus}
          onLoad={() => setFormsComponentLoaded(true)}
          onStatusChange={setFormStatus}
        />
      )}
      <ContinueButton loading={isCheckoutLoading || isFormBusy} onClick={onContinueClick} disabled={isButtonDisabled()}>
        {isCartEnabled
          ? t('plan-customization.widget.button-text-continue-to-cart')
          : t('plan-customization.widget.button-text')}
      </ContinueButton>
      <Text tagName="p" className={classes.disclaimer}>
        {t('plan-customization.widget.disclaimer')}
      </Text>
    </div>
  );
};

const FixedStartDate: FC<{ date: Date }> = ({ date }) => {
  const { locale } = useLocale();
  const { t } = useTranslation();

  return (
    <div className={classes.fixedStartDate} data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_FIXED_START_DATE}>
      <Text className={classes.fixedStartDateLabel} tagName="p">
        {t('plan-customization.widget.start-date-label')}
      </Text>
      <Text className={classes.fixedStartDateValue} tagName="p">
        {fullDate(date, locale)}
      </Text>
    </div>
  );
};

const ContinueButton: FC<{ loading: boolean; children: ReactNode; onClick: () => void; disabled: boolean }> = ({
  loading,
  onClick,
  children,
  disabled,
}) => {
  return (
    <Button
      data-hook={VIEWER_HOOKS.PLAN_CUSTOMIZATION_GO_TO_CHECKOUT_BUTTON}
      hoverStyle={HoverStyles.underline}
      className={st(classes.ctaButton, { loading })}
      fullWidth
      upgrade
      priority={ButtonPriority.primary}
      onClick={onClick}
      disabled={loading || disabled}
    >
      <span className={classes.label}>{children}</span>
      {loading && (
        <div className={classes.loaderContainer}>
          <ThreeDotsLoader className={classes.loader} />
        </div>
      )}
    </Button>
  );
};

export default withClientSideRenderMark(ProductPageWidget);
